import { useEffect } from 'react';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { clearCaregiverImpersonationState } from '@leagueplatform/caregiver';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { clearPendingInitialMessage } from 'common/pending-initial-message';
import {
  getEnvironmentDefinition,
  getPersistedEnvironmentKey,
} from 'common/environments';

export const AuthViewLogout = () => {
  useEffect(() => {
    // Clear any caregiver impersonation state so a caregiver's next sign-in
    // starts from the caregiver shell rather than a stale care recipient.
    clearCaregiverImpersonationState();
    // Belt-and-suspenders alongside the BroadcastChannel-based clear in
    // clear-pending-initial-message-on-sign-out.ts: that one covers *other*
    // open tabs, but depends on BroadcastChannel being supported. This clears
    // the tab actually signing out unconditionally, regardless of that.
    clearPendingInitialMessage();
    StandaloneAuth.client.logout({ returnTo: window.location.origin });
  }, []);

  return <LoadingIndicator />;
};

export const AuthViewLogin = () => {
  useEffect(() => {
    // Clear any caregiver impersonation state before re-authenticating. It
    // lives in sessionStorage, so a same-tab re-auth (session expiry, or
    // hitting `/sign-in` without going through `/sign-out`) would otherwise
    // leave `impersonateeLeagueUserId` set — dropping the caregiver onto the
    // member home (shell check returns false) while stale impersonation is
    // still spread onto auth requests.
    clearCaregiverImpersonationState();

    // A `connection` query param is ignored. Everyone signs in through the
    // environment's single connection, so a link carrying `?connection=Caregivers`
    // — a stale bookmark, or an old invitation email — must not divert the sign-in
    // to a connection that holds no accounts.
    const searchParams = new URLSearchParams(window.location.search);

    // Preserve an optional post-login return path (e.g. the caregiver
    // account-claim deep link that routed here via RequireCaregiverClaimAuth).
    // Only accept an app-relative path — a single leading slash but not `//`
    // (protocol-relative) — so a crafted `redirect_uri` cannot turn this into an
    // open redirect. The landing page navigates to `appState.redirectPath` once
    // authenticated.
    const redirectUri = searchParams.get('redirect_uri');
    const appState =
      redirectUri &&
      redirectUri.startsWith('/') &&
      !redirectUri.startsWith('//')
        ? { appState: { redirectPath: redirectUri } }
        : {};

    // Also set here, rather than leaning on the `redirectLoginOptions` default in
    // config.tsx, so this page's contract is readable on its own.
    const { auth0Connection } = getEnvironmentDefinition(
      getPersistedEnvironmentKey(),
    );
    StandaloneAuth.client.loginWithRedirect(
      auth0Connection
        ? { connection: auth0Connection, ...appState }
        : { ...appState },
    );
  }, []);

  return <LoadingIndicator />;
};
